import React, { useEffect, useState } from "react";
import numeral from "numeral";
import * as fineTuningReducer from "containers/FineTuning/reducer";
import * as aiServiceReducer from "containers/AiService/reducer";
import Slider from "rc-slider";
import { LineInput } from "components/Inputs/LineInput";
import Select from "react-select";
import { useDispatch, useSelector } from "types/store";
import { Button, Accordion } from "aible";
import styles from "./style.module.css";
import { getAiServiceModel, getModelId } from "containers/ChatGPT/reducer";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";
import { keysOf } from "utils/collect";
import { FineTuningExecutionType } from "types/content_generation";
import { customMapper, selectOnChangeWrapperIfPresent } from "utils/select";
import { toast } from "react-toastify";
const TRAINING_DATA_PRESETS = {
    behavior: [4, 64, 256],
    speed: [20, 2, 1],
    adjustments: [0.0001, 0.0002, 0.003],
    batch: [1, 8, 32],
    adapterSize: ["ADAPTER_SIZE_ONE", "ADAPTER_SIZE_FOUR", "ADAPTER_SIZE_EIGHT", "ADAPTER_SIZE_SIXTEEN"]
};
const getPresetValue = (vKey, index) => {
    return TRAINING_DATA_PRESETS[vKey]?.[index];
};
const AI_SERVICE_MODEL_MAPPER = customMapper((b) => b.name);
const EXAMPLE_SET_MAPPER = customMapper((b) => b.name);
const FineTuneTrainingModal = ({ showModal, updateShowModal, updateSelectedDataset, dataset, datasetOptions, selectedModel, updateSelectedModel, isAutoSelect }) => {
    const dispatch = useDispatch();
    const [newModelName, updateNewModelName] = useState("");
    const [behavior, updateBehavior] = useState(1);
    const [speed, updateSpeed] = useState(1);
    const [adjustments, updateAdjustments] = useState(1);
    const [batch, updateBatch] = useState(1);
    const [isLoading, updateIsLoading] = useState(false);
    const [modelName, updateModelName] = useState(selectedModel?.name ?? "");
    const [epochCount, updateEpochCount] = useState(4);
    const [learningRateMulti, updateLearningRateMulti] = useState(1);
    const [adapterSize, updateAdapterSize] = useState(0);
    const [estimatedCosts, updateEstimatedCosts] = useState(undefined);
    const resetFields = () => {
        updateNewModelName("");
        updateBaseModel(baseModelOptions[0]);
        updateIsLoading(false);
        updateModelName(selectedModel?.name ?? "");
        updateEstimatedCosts(undefined);
    };
    useEffect(() => {
        resetFields();
    }, [selectedModel]);
    const aiServiceConnections = useSelector((state) => aiServiceReducer.getConnections(state));
    const aiServiceModels = useSelector((state) => aiServiceReducer.getModels(state));
    const validConnections = Object?.keys(aiServiceConnections)
        ?.map((key) => Number(key))
        .filter((connId) => {
        const validConnectionType = aiServiceConnections[connId]?.type === "VERTEX_AI_GEMINI" ||
            aiServiceConnections[connId]?.type === "NIM_LLM" ||
            aiServiceConnections[connId]?.type === "DGX";
        return validConnectionType && aiServiceConnections[connId]?.status === "ENABLED";
    });
    const chatModelId = useSelector((state) => getModelId(state));
    const baseModelOptions = keysOf(aiServiceModels ?? [])
        .filter((modelId) => {
        // removing allowedExternalIds check to show all models from gemini or nim_llm connection
        return validConnections.includes(aiServiceModels[modelId]?.connection_id ?? -1);
        // return (validConnections.includes(aiServiceModels[modelId]?.connection_id)) && allowedExternalIds.includes(aiServiceModels[modelId].external_id);
    })
        .map((modelId) => {
        return aiServiceModels[modelId];
    });
    const [baseModel, updateBaseModel] = useState(baseModelOptions[0]);
    const aiServiceModel = useSelector((state) => getAiServiceModel(state, chatModelId));
    useEffect(() => {
        if (!showModal && aiServiceModel !== undefined) {
            const chatBaseModel = baseModelOptions.find((o) => o?.id === aiServiceModel.id);
            if (chatBaseModel) {
                updateBaseModel(chatBaseModel);
            }
        }
    }, [aiServiceModels, chatModelId]);
    useEffect(() => {
        if (baseModel && dataset) {
            updateNewModelName(`${baseModel.name}: ${dataset.name}`);
        }
        if (estimatedCosts) {
            updateEstimatedCosts(undefined);
        }
    }, [baseModel, dataset]);
    const sliderHandleStyle = {
        backgroundColor: "#9F85F2",
        borderColor: "white",
        boxShadow: "0px 0px 6px 1px rgba(81, 47, 119, 0.35)",
        zIndex: 100,
        pointer: "cursor !important"
    };
    const handleTrainModel = async (modelName) => {
        let trainingDataOptions = undefined;
        //Currently not supporting mistral and will need to update if re-added
        const showMistralOptions = false; //&& baseModel && baseModel?.value === MODELS.MISTRAL;
        const showGeminiOptions = baseModel && String(baseModel?.external_id).startsWith("gemini");
        if (showMistralOptions) {
            trainingDataOptions = {
                lora_r: getPresetValue("behavior", behavior),
                epochs: getPresetValue("speed", speed),
                adam_alpha: getPresetValue("adjustments", adjustments),
                batch: getPresetValue("batch", batch)
            };
        }
        else if (showGeminiOptions) {
            trainingDataOptions = {
                type: "VERTEX_AI_GEMINI",
                epoch_count: epochCount,
                learning_rate_multiplier: learningRateMulti,
                adapter_size: getPresetValue("adapterSize", adapterSize)
            };
        }
        if (dataset === undefined) {
            return;
        }
        updateIsLoading(true);
        toast("Starting Post Training", {
            type: toast.TYPE.SUCCESS,
            position: toast.POSITION.TOP_CENTER,
            autoClose: 3500
        });
        try {
            const newModel = await dispatch(fineTuningReducer.trainFineTuningExecution({
                base_model_version_id: baseModel?.version_id,
                example_set_id: dataset?.id,
                name: modelName,
                type: FineTuningExecutionType.TRAINING,
                parameters: trainingDataOptions
            }));
            if (newModel) {
                updateSelectedModel(newModel);
            }
        }
        finally {
            updateIsLoading(false);
        }
    };
    // const handleCheckTrainingCosts = async (modelName: string) => {
    //   let trainingDataOptions: FineTuningParameters | undefined = undefined;
    //   //Currently not supporting mistral and will need to update if re-added
    //   const showMistralOptions = false; // && baseModel && baseModel?.value === MODELS.MISTRAL;
    //   const showGeminiOptions = baseModel && String(baseModel?.external_id).startsWith("gemini");
    //
    //   if (showMistralOptions) {
    //     trainingDataOptions = {
    //       lora_r: getPresetValue("behavior", behavior),
    //       epochs: getPresetValue("speed", speed),
    //       adam_alpha: getPresetValue("adjustments", adjustments),
    //       batch: getPresetValue("batch", batch)
    //     };
    //   } else if (showGeminiOptions) {
    //     trainingDataOptions = {
    //       type: "VERTEX_AI_GEMINI",
    //       epoch_count: epochCount,
    //       learning_rate_multiplier: learningRateMulti,
    //       adapter_size: getPresetValue("adapterSize", adapterSize)
    //     };
    //   }
    //   if (dataset === undefined) {
    //     return;
    //   }
    //   updateIsLoading(true);
    //   try {
    //     const costEstimate = await dispatch(
    //       fineTuningReducer.getExecutionCostEstimate(baseModel?.version_id, dataset?.id, modelName, trainingDataOptions)
    //     );
    //     if (costEstimate) {
    //       updateEstimatedCosts(costEstimate);
    //     } else {
    //       updateEstimatedCosts(undefined);
    //     }
    //   } finally {
    //     updateIsLoading(false);
    //   }
    // };
    const getModalTitle = (selectedModel) => {
        if (selectedModel?.status === "SUCCEEDED") {
            return "Your Model Is Ready To Be Used";
        }
        if (selectedModel?.status === "RUNNING") {
            return "Your Model Improvements Are In Progress";
        }
        if (selectedModel?.status === "FAILED") {
            return "There was an error with training your model";
        }
        if (selectedModel?.status === "PENDING") {
            return "Your training request has been created.";
        }
        if (selectedModel?.status === "CANCELLING" || selectedModel?.status === "CANCELLED") {
            return "Your training request has been canceled.";
        }
        return "Post Train A Model";
    };
    // Currently not supporting mistral and will need to be updated if re-added
    const showMistralOptions = false; // && baseModel && baseModel?.value === MODELS.MISTRAL;
    const showGeminiOptions = baseModel && String(baseModel?.external_id).startsWith("gemini");
    const handleUpdateName = (newName) => {
        if (selectedModel === undefined) {
            return;
        }
        try {
            dispatch(fineTuningReducer.updateFineTuningExecution(selectedModel.id, { name: newName }));
        }
        finally {
            updateShowModal(false);
        }
    };
    const foundDataset = datasetOptions?.find((option) => option?.id === dataset?.id);
    return (React.createElement(Dialog, { open: showModal, onOpenChange: () => {
            updateShowModal(false);
            resetFields();
        } },
        React.createElement(DialogContent, null,
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, getModalTitle(selectedModel))),
            React.createElement("div", null,
                foundDataset && (React.createElement("div", { className: styles.feedbackDataContainer },
                    React.createElement("div", { className: styles.feedbackDataLikes }, foundDataset.result_count),
                    React.createElement("div", { className: styles.feedbackDataBoldText, style: { marginTop: "-4px" } }, "Total feedback will be used to improve a new AI model"))),
                !isAutoSelect && (React.createElement("div", { className: styles.trainingOptionRow },
                    React.createElement("div", { className: styles.trainingOptionLabel }, "Training Dataset"),
                    React.createElement("div", { className: styles.trainingInput },
                        React.createElement(Select, { options: EXAMPLE_SET_MAPPER.mapAll(datasetOptions), value: EXAMPLE_SET_MAPPER.map(foundDataset), onChange: selectOnChangeWrapperIfPresent(updateSelectedDataset), placeholder: "Select a dataset", menuPlacement: "bottom", styles: {
                                menu: (provided) => ({
                                    ...provided,
                                    zIndex: 101
                                })
                            } })))),
                React.createElement("div", { className: styles.trainingOptionRow, style: { marginBottom: "8px" } },
                    React.createElement("div", { className: styles.trainingOptionLabel }, "Base Model to Improve"),
                    React.createElement("div", { className: styles.trainingInput },
                        React.createElement(Select, { options: AI_SERVICE_MODEL_MAPPER.mapAll(baseModelOptions), value: AI_SERVICE_MODEL_MAPPER.map(baseModel), onChange: selectOnChangeWrapperIfPresent(updateBaseModel), placeholder: "Select a Base Model to Fine Tune", menuPlacement: "bottom", styles: {
                                menu: (provided) => ({
                                    ...provided,
                                    zIndex: 101
                                })
                            } }))),
                React.createElement("div", { className: styles.trainingOptionRow },
                    React.createElement("div", { className: styles.trainingOptionLabel }, "New Model Name"),
                    React.createElement("div", { className: styles.trainingInput },
                        React.createElement(LineInput, { value: newModelName, onChange: (val) => updateNewModelName(val), style: { width: "100%" }, inputStyle: { padding: "8px 10px", borderRadius: "4px" }, updateInstant: true, placeholder: "Please name your new fine tuning model" }))),
                showMistralOptions && (React.createElement(MistralOptions, { speed: speed, updateSpeed: updateSpeed, behavior: behavior, updateBehavior: updateBehavior, adjustments: adjustments, updateAdjustments: updateAdjustments, batch: batch, updateBatch: updateBatch, sliderHandleStyle: sliderHandleStyle })),
                showGeminiOptions && (React.createElement(GeminiOptions, { epochCount: epochCount, updateEpochCount: updateEpochCount, learningRateMulti: learningRateMulti, updateLearningRateMulti: updateLearningRateMulti, sliderHandleStyle: sliderHandleStyle, speed: speed, updateSpeed: updateSpeed, adapterSize: adapterSize, updateAdapterSize: updateAdapterSize })),
                React.createElement("div", { className: styles.trainingCostEstimateWrapper }),
                estimatedCosts && (React.createElement("div", { className: styles.trainingCostEstimate },
                    React.createElement("div", { className: styles.trainingCostItem },
                        React.createElement("div", null, "Estimated cost: $"),
                        React.createElement("div", { style: { fontWeight: "700" } }, numeral(estimatedCosts?.estimated_fine_tuning_cost).format("0.00[00]"))),
                    React.createElement("div", { className: styles.trainingCostItem },
                        React.createElement("div", null, "Data Tokens: "),
                        React.createElement("div", { style: { fontWeight: "700" } }, estimatedCosts?.example_set_billable_token_count)),
                    React.createElement("div", { className: styles.trainingCostItem },
                        React.createElement("div", null, "Rate / million tokens: $"),
                        React.createElement("div", { style: { fontWeight: "700" } }, estimatedCosts?.rate_per_token))))),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { onClick: () => {
                        resetFields();
                        updateShowModal(false);
                    } }, "Close"),
                selectedModel /** TODO: add a loading state. Maybe a cancel button? */ ? (React.createElement(React.Fragment, null)) : (React.createElement(Button, { onClick: () => {
                        if (newModelName !== undefined) {
                            handleTrainModel(newModelName);
                            updateEstimatedCosts(undefined);
                            updateShowModal(false);
                        }
                    }, type: "primary", 
                    // Removed min rows check
                    disabled: !baseModel || !newModelName, 
                    // disabled={!baseModel || !newModelName || !(dataset?.totalFeedback >= FEEDBACK_BREAKPOINTS.MIN || dataset?.result_count >= FEEDBACK_BREAKPOINTS.MIN)}
                    isLoading: isLoading }, "Start AI Training")),
                selectedModel && selectedModel?.status === "SUCCEEDED" && (React.createElement(Button, { onClick: () => handleUpdateName(modelName), type: "primary", disabled: !modelName || modelName === selectedModel?.name, isLoading: isLoading }, "Save Changes"))))));
};
const MistralOptions = ({ speed, updateSpeed, behavior, updateBehavior, adjustments, updateAdjustments, batch, updateBatch, sliderHandleStyle }) => {
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.trainingOption, style: { marginTop: "28px" } },
            React.createElement("div", { className: styles.sliderLabel }, "How fast would you like the training to complete"),
            React.createElement("div", { className: styles.sliderHolder },
                React.createElement(Slider, { marks: {
                        0: "High-Quality Training",
                        1: "Balanced",
                        2: "Fast Training"
                    }, min: 0, max: 2, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => {
                        updateSpeed(val);
                        if (val === 0) {
                            updateBehavior(2);
                            updateAdjustments(2);
                            updateBatch(0);
                        }
                        else if (val === 2) {
                            updateBehavior(0);
                            updateAdjustments(0);
                            updateBatch(2);
                        }
                        else {
                            updateBehavior(1);
                            updateAdjustments(1);
                            updateBatch(1);
                        }
                    }, value: speed }))),
        React.createElement(Accordion, { header: React.createElement("div", { className: styles.accordionHeader }, "Advanced Options") },
            React.createElement("div", { className: styles.trainingOption },
                React.createElement("div", { className: styles.sliderLabel }, "How much would you like the model behavior to change?"),
                React.createElement("div", { className: styles.sliderHolder },
                    React.createElement(Slider, { marks: {
                            0: "VERY LITTLE",
                            1: "",
                            2: "SIGNIFICANTLY"
                        }, min: 0, max: 2, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => updateBehavior(val), value: behavior }))),
            React.createElement("div", { className: styles.trainingOption },
                React.createElement("div", { className: styles.sliderLabel }, "Fine Grained Adjustments"),
                React.createElement("div", { className: styles.sliderHolder },
                    React.createElement(Slider, { marks: {
                            0: "LOW RESOLUTION",
                            1: "",
                            2: "HIGH RESOLUTION"
                        }, min: 0, max: 2, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => updateAdjustments(val), value: adjustments }))),
            React.createElement("div", { className: styles.trainingOption },
                React.createElement("div", { className: styles.sliderLabel }, "Batch Size"),
                React.createElement("div", { className: styles.sliderHolder },
                    React.createElement(Slider, { marks: {
                            0: "SMALL",
                            1: "",
                            2: "LARGE"
                        }, min: 0, max: 2, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => updateBatch(val), value: batch }))))));
};
const GeminiOptions = ({ epochCount, updateEpochCount, learningRateMulti, updateLearningRateMulti, speed, updateSpeed, adapterSize, updateAdapterSize, sliderHandleStyle }) => {
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { style: { marginTop: "28px" } },
            React.createElement("div", { className: styles.sliderLabel }, "How fast would you like the training to complete"),
            React.createElement("div", { className: styles.sliderHolder },
                React.createElement(Slider, { marks: {
                        0: "High-Quality Training",
                        1: "Balanced",
                        2: "Fast Training"
                    }, min: 0, max: 2, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => {
                        updateSpeed(val);
                        if (val === 0) {
                            updateAdapterSize(1);
                            updateLearningRateMulti(1);
                        }
                        else if (val === 2) {
                            updateAdapterSize(0);
                            updateLearningRateMulti(4);
                        }
                        else {
                            updateAdapterSize(0);
                            updateLearningRateMulti(1);
                        }
                    }, value: speed })),
            React.createElement(Accordion, { header: React.createElement("div", { className: styles.accordionHeader }, "Advanced Options") },
                React.createElement("div", { className: styles.trainingOption },
                    React.createElement("div", { className: styles.sliderLabelWithInput }, "Adapter Size"),
                    React.createElement("div", { className: styles.sliderHolder },
                        React.createElement(Slider, { marks: {
                                0: "1",
                                1: "4",
                                2: "8",
                                3: "16"
                            }, min: 0, max: 3, step: 1, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => {
                                updateAdapterSize(val);
                            }, value: adapterSize }))),
                React.createElement("div", { className: styles.trainingOption, style: { display: "none" } },
                    React.createElement("div", { className: styles.sliderLabelWithInput },
                        "Epoch Count",
                        React.createElement("div", { style: { width: "60px" } },
                            React.createElement(LineInput, { value: epochCount, onChange: (val) => {
                                    updateEpochCount(Math.max(Math.min(Number(val), 50), 1));
                                } }))),
                    React.createElement("div", { className: styles.sliderHolder },
                        React.createElement(Slider, { marks: {
                                1: "1",
                                50: "50"
                            }, min: 1, max: 50, step: 1, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => updateEpochCount(val), value: epochCount }))),
                React.createElement("div", { className: styles.trainingOption },
                    React.createElement("div", { className: styles.sliderLabelWithInput },
                        "Learning Rate Multiplier",
                        React.createElement("div", { style: { width: "60px" } },
                            React.createElement(LineInput, { value: learningRateMulti, onChange: (val) => {
                                    updateLearningRateMulti(Math.max(Math.min(Number(val), 4), 0.02));
                                } }))),
                    React.createElement("div", { className: styles.sliderHolder },
                        React.createElement(Slider, { marks: {
                                [0.02]: ".02",
                                1: "1",
                                4: "4"
                            }, min: 0, max: 4, step: 0.02, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => updateLearningRateMulti(val), value: learningRateMulti })))))));
};
export default FineTuneTrainingModal;
